import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import ProductDialog from '../components/ProductDialog';
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Stack
} from '@mui/material';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragEndEvent 
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import productService from '../services/productService';
import categoryService from '../services/categoryService';
import { Product, NewProduct, ProductFilter } from '../models/Product';
import { Category } from '../models/Category';
import { useAdminScope } from '../contexts/AdminScopeContext';
import ScopeRequirementAlert from '../components/ScopeRequirementAlert';
import {
  isMenuCatalogManagedByPosCaisse,
  MENU_CATALOG_POSCAISSE_MESSAGE,
} from '../utils/menuCatalogOwnership';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const matchesProductCategory = (product: Product, categoryId: string) => {
  const desired = categoryId.trim().toLowerCase();
  if (!desired) return true;

  return (
    (product.categoryId || '').trim().toLowerCase() === desired ||
    (product.categoryKey || '').trim().toLowerCase() === desired ||
    (product.category || '').trim().toLowerCase() === desired
  );
};

const applyProductsFilter = (items: Product[], activeFilter: ProductFilter): Product[] => {
  let filtered = [...items];

  if (activeFilter.category && !activeFilter.isOnPromotionOnly) {
    filtered = filtered.filter((product) => matchesProductCategory(product, activeFilter.category!));
  }

  if (activeFilter.searchTerm) {
    const term = activeFilter.searchTerm.toLowerCase();
    filtered = filtered.filter((product) =>
      product.name.toLowerCase().includes(term) ||
      product.description?.toLowerCase().includes(term) ||
      product.ingredients?.some((ingredient) => ingredient.toLowerCase().includes(term)) ||
      product.tags?.some((tag) => tag.toLowerCase().includes(term))
    );
  }

  if (activeFilter.availableOnly) {
    filtered = filtered.filter((product) => product.isAvailable);
  }

  if (activeFilter.featuredOnly) {
    filtered = filtered.filter((product) => product.isFeatured);
  }

  if (activeFilter.tagFilter) {
    filtered = filtered.filter((product) => product.tags?.includes(activeFilter.tagFilter!));
  }

  if (activeFilter.isOnPromotionOnly) {
    filtered = filtered.filter((product) => product.isOnPromotion === true);
  }

  filtered.sort((a, b) => {
    const primaryA = activeFilter.category ? (a.categoryOrder ?? 0) : (a.order ?? 0);
    const primaryB = activeFilter.category ? (b.categoryOrder ?? 0) : (b.order ?? 0);
    const primaryDiff = primaryA - primaryB;
    if (primaryDiff !== 0) return primaryDiff;
    return a.name.localeCompare(b.name);
  });

  return filtered;
};

const mergeSubsetIntoAllProducts = (baseProducts: Product[], updatedSubset: Product[]) => {
  const updates = new Map(updatedSubset.map((product) => [product.id, product]));
  return baseProducts.map((product) => updates.get(product.id) ?? product);
};

const mergeProductPatch = (currentProduct: Product, patch: Partial<Product>): Product => {
  const hasExplicitIsActive = Object.prototype.hasOwnProperty.call(patch, 'isActive');
  const hasExplicitIsVisibleOnline = Object.prototype.hasOwnProperty.call(patch, 'isVisibleOnline');
  const hasExplicitIsAvailable = Object.prototype.hasOwnProperty.call(patch, 'isAvailable');

  const isActive = hasExplicitIsActive
    ? patch.isActive
    : currentProduct.isActive ?? true;

  const isVisibleOnline = hasExplicitIsVisibleOnline
    ? patch.isVisibleOnline
    : currentProduct.isVisibleOnline ?? true;

  const derivedAvailability = Boolean(isActive && isVisibleOnline);
  const isAvailable =
    hasExplicitIsAvailable && !hasExplicitIsActive && !hasExplicitIsVisibleOnline
      ? Boolean(patch.isAvailable)
      : derivedAvailability;

  return {
    ...currentProduct,
    ...patch,
    isActive,
    isVisibleOnline,
    isAvailable,
  };
};

const getProductOnlineStatus = (product: Product) => {
  if (product.isActive === false) {
    return { label: 'Inactif catalogue', color: 'default' as const };
  }
  if (product.isVisibleOnline === false) {
    return { label: 'Masqué online', color: 'warning' as const };
  }
  if (product.isAvailable === false) {
    return { label: 'Indisponible', color: 'error' as const };
  }
  return { label: 'Disponible', color: 'success' as const };
};

const isProductOrderableOnline = (product: Product) =>
  product.isActive !== false &&
  product.isVisibleOnline !== false &&
  product.isAvailable !== false;

// SortableRow Component - pour le glisser-déposer
interface SortableRowProps {
  id: string;
  product: Product;
  categoryName: string;
  onToggleAvailability: (product: Product) => void;
  onToggleFeatured: (product: Product) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onDuplicate: (product: Product) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  readOnly: boolean;
}

const SortableRow: React.FC<SortableRowProps> = ({ 
  id, 
  product, 
  categoryName,
  onToggleAvailability, 
  onToggleFeatured,
  onEdit, 
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  index,
  isFirst,
  isLast,
  readOnly
}) => {
  // Utiliser le hook useSortable pour rendre la ligne déplaçable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled: readOnly });
  const onlineStatus = getProductOnlineStatus(product);
  const orderableOnline = isProductOrderableOnline(product);

  // Appliquer les styles de transformation pendant le glisser-déposer
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell padding="checkbox">
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          disabled={readOnly}
          sx={{ cursor: readOnly ? 'default' : 'grab' }}
        >
          <DragIndicatorIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        {product.imageUrl ? (
          <Box 
            component="img"
            src={product.imageUrl}
            alt={product.name}
            sx={{ width: 50, height: 50, objectFit: 'contain' }}
          />
        ) : (
          <Box
            sx={{
              width: 50,
              height: 50,
              bgcolor: 'grey.200',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 1
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No img
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell>
        <Typography fontWeight="medium">{product.name}</Typography>
        {product.description && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {product.description.substring(0, 50)}{product.description.length > 50 ? '...' : ''}
          </Typography>
        )}
        {product.isFeatured && (
          <Chip 
            size="small" 
            icon={<StarIcon fontSize="small" />} 
            label="Produit vedette" 
            color="warning" 
            sx={{ mt: 0.5 }}
          />
        )}
      </TableCell>
      <TableCell>{categoryName}</TableCell>
      <TableCell>{product.price.toFixed(2)} MAD</TableCell>
      <TableCell>
        {product.priceXL !== undefined ? (
          <Typography variant="body2" color="text.primary">
            {product.priceXL.toFixed(2)} MAD
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </TableCell>
      <TableCell> {/* New cell for Promotion Price */}
        {product.isOnPromotion && product.discountPrice !== undefined ? (
          <Typography variant="body2" color="text.primary">
            {product.discountPrice.toFixed(2)} MAD
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={onlineStatus.color}
          label={onlineStatus.label}
          variant={onlineStatus.color === 'success' ? 'filled' : 'outlined'}
        />
      </TableCell>
      <TableCell>
        {product.customizationSections && product.customizationSections.length > 0 ? (
          <Typography variant="caption">
            {product.customizationSections.length} section(s)
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Aucune
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Monter">
            <span>
              <IconButton
                size="small"
                color="default"
                onClick={() => onMoveUp(index)}
                disabled={readOnly || isFirst}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Descendre">
            <span>
              <IconButton
                size="small"
                color="default"
                onClick={() => onMoveDown(index)}
                disabled={readOnly || isLast}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={product.isFeatured ? "Retirer des vedettes" : "Ajouter aux vedettes"}>
            <IconButton
              color={product.isFeatured ? "warning" : "default"}
              onClick={() => onToggleFeatured(product)}
              size="small"
              disabled={readOnly}
            >
              {product.isFeatured ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={orderableOnline ? "Marquer comme indisponible" : "Rendre visible et disponible"}>
            <IconButton
              color={orderableOnline ? "success" : "error"}
              onClick={() => onToggleAvailability(product)}
              size="small"
              disabled={readOnly}
            >
              {orderableOnline ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
          <IconButton
            color="primary"
            onClick={() => onEdit(product)}
            size="small"
            disabled={readOnly}
          >
            <EditIcon />
          </IconButton>
          <Tooltip title="Dupliquer le produit">
            <IconButton
              color="info"
              onClick={() => onDuplicate(product)}
              size="small"
              disabled={readOnly}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            color="error"
            onClick={() => onDelete(id)}
            size="small"
            disabled={readOnly}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const Products: React.FC = () => {
  const { activeScopeType, selectedStoreId, selectedStore } = useAdminScope();
  const isCatalogReadOnly = isMenuCatalogManagedByPosCaisse(selectedStore);
  // États pour les produits, catégories et filtres
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // État pour la pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // État pour les filtres
  const [filter, setFilter] = useState<ProductFilter>({});
  
  // Relation entre produits et noms de catégories
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  
  // États pour le dialogue d'ajout/édition de produit
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>(undefined);
  
  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const products = useMemo(
    () => applyProductsFilter(allProducts, filter),
    [allProducts, filter]
  );

  // Charger les produits et les catégories au chargement de la page
  useEffect(() => {
    if (activeScopeType === 'store' && selectedStoreId) {
      void loadCategoriesAndProducts();
      return;
    }

    setCategories([]);
    setCategoryMap({});
    setAllProducts([]);
    setTotalProducts(0);
    setLoading(false);
  }, [activeScopeType, selectedStoreId]);

  useEffect(() => {
    setTotalProducts(products.length);
    setPage((currentPage) => {
      const maxPage = products.length === 0 ? 0 : Math.max(0, Math.ceil(products.length / rowsPerPage) - 1);
      return Math.min(currentPage, maxPage);
    });
  }, [products.length, rowsPerPage]);

  // Fonction pour charger les catégories puis les produits
  const loadCategoriesAndProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Charger les catégories
      const categoriesData = await categoryService.getCategories();
      setCategories(categoriesData);
      
      // Créer une map des IDs de catégories vers les noms
      const catMap: Record<string, string> = {};
      categoriesData.forEach(cat => {
        catMap[cat.id] = cat.name;
      });
      setCategoryMap(catMap);
      
      // Charger les produits
      await loadProducts();
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError('Erreur lors du chargement des données. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Fonction pour charger les produits avec filtres
  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await productService.getProducts();
      setAllProducts(data);
      extractAllTags(data);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
      setError('Erreur lors du chargement des produits. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  // Gestionnaire de changement de page
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Gestionnaire de changement du nombre d'éléments par page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Retour à la première page
  };
  
  // Obtenir les produits de la page courante
  const paginatedProducts = products.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // Fonction pour extraire tous les tags uniques des produits
  const extractAllTags = (products: Product[]) => {
    const tagsSet = new Set<string>();
    products.forEach(product => {
      if (product.tags?.length) {
        product.tags.forEach(tag => tagsSet.add(tag));
      }
    });
    setAllTags(Array.from(tagsSet).sort());
  };
  
  // Gérer les changements de filtre de catégorie
  const handleCategoryFilterChange = (selectedCategoryId: string | "") => {
    setPage(0);
    const isPromotionsCategory = selectedCategoryId === 'promotions';

    setFilter(prev => ({
      ...prev,
      category: selectedCategoryId || undefined,
      isOnPromotionOnly: isPromotionsCategory ? true : undefined
    }));
  };
  
  // Gérer les changements de filtre de recherche
  const handleSearchTermChange = (term: string) => {
    setPage(0);
    setFilter(prev => ({
      ...prev,
      searchTerm: term || undefined
    }));
  };
  
  // Gérer les changements de filtre de disponibilité
  const handleAvailableFilterChange = (availableOnly: boolean) => {
    setPage(0);
    setFilter(prev => ({
      ...prev,
      availableOnly
    }));
  };
  
  // Gérer les changements de filtre de produits vedettes
  const handleFeaturedFilterChange = (featuredOnly: boolean) => {
    setPage(0);
    setFilter(prev => ({
      ...prev,
      featuredOnly
    }));
  };
  
  // Gérer les changements de filtre par tag
  const handleTagFilterChange = (tag: string | "") => {
    setPage(0);
    setFilter(prev => ({
      ...prev,
      tagFilter: tag || undefined
    }));
  };
  
  // Gestionnaire pour ouvrir le dialogue d'ajout
  const handleAddProduct = () => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setEditingProduct(undefined);
    setProductDialogOpen(true);
  };

  // Gestionnaire pour la modification d'un produit
  const handleEditProduct = (product: Product) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setEditingProduct(product);
    setProductDialogOpen(true);
  };
  
  // Gestionnaire pour fermer le dialogue
  const handleCloseProductDialog = () => {
    setProductDialogOpen(false);
  };
  
  // Gestionnaire pour sauvegarder un produit
  const handleSaveProduct = async (productData: Product | NewProduct, imageFile?: File) => {
    if (isCatalogReadOnly) {
      throw new Error(MENU_CATALOG_POSCAISSE_MESSAGE);
    }
    try {
      let savedProduct: Product;
      if ('id' in productData) {
        // Mise à jour d'un produit existant, inclure image si présent
        savedProduct = await productService.updateProduct(productData.id, productData, imageFile);
      } else {
        // Création d'un nouveau produit, inclure image si présent
        savedProduct = await productService.createProduct(productData, imageFile);
      }

      setAllProducts((previousProducts) => {
        const nextProducts = previousProducts.filter((product) => product.id !== savedProduct.id);
        nextProducts.push(savedProduct);
        return nextProducts;
      });
      
      // Reset editing product state after successful save
      setEditingProduct(undefined);

    } catch (err) {
      console.error('Erreur lors de la sauvegarde du produit:', err);
      throw err;
    }
  };
  
  // Gestionnaire pour la duplication d'un produit
  const handleDuplicateProduct = async (product: Product) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    try {
      setLoading(true);
      
      // Créer une copie du produit sans l'id
      const newProduct: NewProduct = {
        name: `${product.name} (copie)`,
        category: product.category,
        description: product.description,
        ingredients: product.ingredients,
        price: product.price,
        isAvailable: product.isAvailable,
        isFeatured: product.isFeatured,
        customizationSections: product.customizationSections,
        tags: product.tags, // Copier les tags s'ils existent
      };
      
      // Créer le nouveau produit
      const createdProduct = await productService.createProduct(newProduct);
      
      // Si le produit original a une image, la copier pour le nouveau produit
      if (product.imageUrl) {
        try {
          // Récupérer l'image originale et la télécharger pour le nouveau produit
          const response = await fetch(product.imageUrl);
          const blob = await response.blob();
          const file = new File([blob], "product_image.jpg", { type: "image/jpeg" });
          
          await productService.uploadProductImage(createdProduct.id, file);
        } catch (imageError) {
          console.error("Erreur lors de la copie de l'image:", imageError);
        }
      }
      
      // Recharger les produits
      await loadProducts();
      
    } catch (err) {
      console.error('Erreur lors de la duplication du produit:', err);
      setError('Erreur lors de la duplication du produit. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  // Gestionnaire pour la suppression d'un produit
  const handleDeleteProduct = async (id: string) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await productService.deleteProduct(id);
        setAllProducts((previousProducts) => previousProducts.filter((product) => product.id !== id));
      } catch (err) {
        console.error('Erreur lors de la suppression du produit:', err);
        setError('Erreur lors de la suppression du produit. Veuillez réessayer.');
      }
    }
  };
  
  // Gestionnaire pour changer la disponibilité d'un produit
  const handleToggleAvailability = async (product: Product) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    const nextAvailability = !isProductOrderableOnline(product);
    const availabilityPatch: Partial<Product> = nextAvailability
      ? {
          isActive: true,
          isVisibleOnline: true,
          isAvailable: true,
        }
      : {
          isAvailable: false,
        };

    setAllProducts((previousProducts) =>
      previousProducts.map((currentProduct) =>
        currentProduct.id === product.id
          ? mergeProductPatch(currentProduct, availabilityPatch)
          : currentProduct
      )
    );

    try {
      const updatedProduct = await productService.updateProduct(
        product.id,
        availabilityPatch
      );
      setAllProducts((previousProducts) =>
        previousProducts.map((currentProduct) =>
          currentProduct.id === updatedProduct.id
            ? mergeProductPatch(updatedProduct, availabilityPatch)
            : currentProduct
        )
      );
    } catch (err) {
      console.error('Erreur lors du changement de disponibilité:', err);
      setError('Erreur lors du changement de disponibilité. Veuillez réessayer.');
      setAllProducts((previousProducts) =>
        previousProducts.map((currentProduct) =>
          currentProduct.id === product.id ? product : currentProduct
        )
      );
    }
  };
  
  // Gestionnaire pour changer la mise en avant d'un produit
  const handleToggleFeatured = async (product: Product) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    try {
      const updatedProduct = await productService.updateProduct(product.id, {
        isFeatured: !product.isFeatured
      });
      setAllProducts((previousProducts) =>
        previousProducts.map((currentProduct) =>
          currentProduct.id === updatedProduct.id ? updatedProduct : currentProduct
        )
      );
    } catch (err) {
      console.error('Erreur lors du changement de mise en avant:', err);
      setError('Erreur lors du changement de mise en avant. Veuillez réessayer.');
    }
  };
  
  // Déplacer un produit vers le haut
  const handleMoveUp = async (index: number) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    if (index <= 0) return; // Ne rien faire si déjà en haut

    const previousAllProducts = allProducts;
    try {
      setSavingOrder(true);
      const newProducts = [...products];
      
      // Échanger avec l'élément précédent
      const current = newProducts[index];
      const previous = newProducts[index - 1];
      
      // Appliquer localement
      [newProducts[index - 1], newProducts[index]] = [newProducts[index], newProducts[index - 1]];
      setAllProducts(mergeSubsetIntoAllProducts(allProducts, newProducts));
      
      // Déterminer si on réorganise dans une catégorie ou globalement
      if (filter.category) {
        // Réorganisation dans une catégorie spécifique
        const updates = [
          { id: current.id, categoryOrder: previous.categoryOrder ?? index - 1 },
          { id: previous.id, categoryOrder: current.categoryOrder ?? index }
        ];
        
        // Utiliser la nouvelle fonction pour réorganiser dans la catégorie
        await productService.reorderProductsInCategory(updates);
      } else {
        // Réorganisation globale
        const updates = [
          { id: current.id, order: previous.order ?? index - 1 },
          { id: previous.id, order: current.order ?? index }
        ];
        
        // Utiliser la fonction existante pour l'ordre global
        await productService.reorderProducts(updates);
      }
    } catch (err) {
      console.error('Erreur lors du déplacement du produit:', err);
      setError('Erreur lors du déplacement. Veuillez réessayer.');
      setAllProducts(previousAllProducts);
    } finally {
      setSavingOrder(false);
    }
  };
  
  // Déplacer un produit vers le bas
  const handleMoveDown = async (index: number) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    if (index >= products.length - 1) return; // Ne rien faire si déjà en bas

    const previousAllProducts = allProducts;
    try {
      setSavingOrder(true);
      const newProducts = [...products];
      
      // Échanger avec l'élément suivant
      const current = newProducts[index];
      const next = newProducts[index + 1];
      
      // Appliquer localement
      [newProducts[index], newProducts[index + 1]] = [newProducts[index + 1], newProducts[index]];
      setAllProducts(mergeSubsetIntoAllProducts(allProducts, newProducts));
      
      // Déterminer si on réorganise dans une catégorie ou globalement
      if (filter.category) {
        // Réorganisation dans une catégorie spécifique
        const updates = [
          { id: current.id, categoryOrder: next.categoryOrder ?? index + 1 },
          { id: next.id, categoryOrder: current.categoryOrder ?? index }
        ];
        
        // Utiliser la nouvelle fonction pour réorganiser dans la catégorie
        await productService.reorderProductsInCategory(updates);
      } else {
        // Réorganisation globale
        const updates = [
          { id: current.id, order: next.order ?? index + 1 },
          { id: next.id, order: current.order ?? index }
        ];
        
        // Utiliser la fonction existante pour l'ordre global
        await productService.reorderProducts(updates);
      }
    } catch (err) {
      console.error('Erreur lors du déplacement du produit:', err);
      setError('Erreur lors du déplacement. Veuillez réessayer.');
      setAllProducts(previousAllProducts);
    } finally {
      setSavingOrder(false);
    }
  };
  
  // Gestionnaire de fin de glisser-déposer
  const handleDragEnd = async (event: DragEndEvent) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    const { active, over } = event;
    
    if (!over) return;
    if (active.id === over.id) return;

    const previousAllProducts = allProducts;
    try {
      setSavingOrder(true);
      
      // Trouver les positions
      const oldIndex = products.findIndex(p => p.id === active.id);
      const newIndex = products.findIndex(p => p.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return;
      
      // Réorganiser le tableau localement
      const newProducts = arrayMove(products, oldIndex, newIndex);
      setAllProducts(mergeSubsetIntoAllProducts(allProducts, newProducts));
      
      // Déterminer si on réorganise dans une catégorie ou globalement
      if (filter.category) {
        // Réorganisation dans une catégorie spécifique
        const updates = newProducts.map((product, index) => ({
          id: product.id,
          categoryOrder: index
        }));
        
        // Utiliser la nouvelle fonction pour réorganiser dans la catégorie
        await productService.reorderProductsInCategory(updates);
      } else {
        // Réorganisation globale
        const updates = newProducts.map((product, index) => ({
          id: product.id,
          order: index
        }));
        
        // Utiliser la fonction existante pour l'ordre global
        await productService.reorderProducts(updates);
      }
    } catch (err) {
      console.error('Erreur lors de la réorganisation:', err);
      setError('Erreur lors de la réorganisation. Veuillez réessayer.');
      setAllProducts(previousAllProducts);
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <Layout title="Gestion des Produits">
      {activeScopeType !== 'store' || !selectedStoreId ? (
        <ScopeRequirementAlert
          requiredScope="store"
          title="Contexte magasin requis"
          description="Cette page gère les produits d'un magasin précis. Sélectionne un scope Magasin depuis la barre haute."
        />
      ) : (
        <>
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">
            Produits
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddProduct}
            disabled={isCatalogReadOnly}
          >
            Ajouter un produit
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Gérez les produits de votre menu.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isCatalogReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {MENU_CATALOG_POSCAISSE_MESSAGE}
        </Alert>
      )}

      {/* Filtres */}
      <Box mb={3} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="category-filter-label">Filtrer par catégorie</InputLabel>
          <Select
            labelId="category-filter-label"
            value={filter.category || ""}
            label="Filtrer par catégorie"
            onChange={(e) => handleCategoryFilterChange(e.target.value)}
          >
            <MenuItem value="">Toutes les catégories</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {allTags.length > 0 && (
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="tag-filter-label">Filtrer par tag</InputLabel>
            <Select
              labelId="tag-filter-label"
              value={filter.tagFilter || ""}
              label="Filtrer par tag"
              onChange={(e) => handleTagFilterChange(e.target.value)}
            >
              <MenuItem value="">Tous les tags</MenuItem>
              {allTags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        <TextField
          label="Rechercher"
          variant="outlined"
          size="small"
          value={filter.searchTerm || ""}
          onChange={(e) => handleSearchTermChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        
        <Stack direction="row" spacing={1}>
          <Chip 
            label="Produits disponibles" 
            color={filter.availableOnly ? "success" : "default"}
            onClick={() => handleAvailableFilterChange(!filter.availableOnly)}
            variant={filter.availableOnly ? "filled" : "outlined"}
          />
          <Chip 
            icon={<StarIcon fontSize="small" />}
            label="Produits vedettes" 
            color={filter.featuredOnly ? "warning" : "default"}
            onClick={() => handleFeaturedFilterChange(!filter.featuredOnly)}
            variant={filter.featuredOnly ? "filled" : "outlined"}
          />
        </Stack>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : savingOrder ? (
        <Box display="flex" justifyContent="center" my={4} flexDirection="column" alignItems="center">
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography>Réorganisation des produits en cours...</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" width="48px"></TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Prix</TableCell>
                <TableCell>Prix XL</TableCell>
                <TableCell>Prix Promotion</TableCell> {/* New column header */}
                <TableCell>Disponible</TableCell>
                <TableCell>Customisations</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Aucun produit trouvé.
                  </TableCell>
                </TableRow>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={paginatedProducts.map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {paginatedProducts.map((product, index) => {
                      const categoryName =
                        categoryMap[product.category] ||
                        categories.find(cat => cat.name.toUpperCase() === product.category.toUpperCase())?.name ||
                        product.category ||
                        'Catégorie inconnue';
                      
                      return (
                        <SortableRow
                          key={product.id}
                          id={product.id}
                          product={product}
                          categoryName={categoryName}
                          onToggleAvailability={handleToggleAvailability}
                          onToggleFeatured={handleToggleFeatured}
                          onEdit={handleEditProduct}
                          onDelete={handleDeleteProduct}
                          onDuplicate={handleDuplicateProduct}
                          onMoveUp={handleMoveUp}
                          onMoveDown={handleMoveDown}
                          index={index}
                          isFirst={index === 0}
                          isLast={index === products.length - 1}
                          readOnly={isCatalogReadOnly}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Composant de pagination */}
      {!loading && !savingOrder && products.length > 0 && (
        <TablePagination
          component="div"
          count={totalProducts}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Éléments par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
        />
      )}

      {/* Dialogue d'ajout/modification de produit */}
      <ProductDialog
        open={productDialogOpen}
        onClose={handleCloseProductDialog}
        onSave={handleSaveProduct}
        product={editingProduct}
        categories={categories}
      />
        </>
      )}
    </Layout>
  );
};

export default Products;
